import React, { useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { getCardStyle } from '../utils/cardStyle';
import OpenCardFaceBackground from './OpenCardFaceBackground';
import { getOpenCardFaceClasses } from '../utils/openCardFace';
import { buildContextFromCard, renderMessageTemplate } from '../utils/messageTemplate';

const CardDetail = ({ card, onBack, onDelete }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  if (!card) {
    return <p className="wallet-empty">Карта не найдена</p>;
  }

  const style = getCardStyle(card, { context: 'detail' });
  const { textColor, labelColor } = style;
  const backTextRendered = renderMessageTemplate(
    style.backText,
    buildContextFromCard(card, style),
  );

  const formatBackText = (text) => {
    if (!text) return 'Дополнительная информация не указана';
    return text.split('\n').map((line, lineIndex) => {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const parts = line.split(urlRegex);
      return (
        <React.Fragment key={lineIndex}>
          {parts.map((part, partIndex) => (
            urlRegex.test(part) ? (
              <a
                key={partIndex}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                className="open-card-link"
                style={{ color: textColor }}
                onClick={(e) => e.stopPropagation()}
              >
                {part}
              </a>
            ) : part
          ))}
          <br />
        </React.Fragment>
      );
    });
  };

  const faceProps = {
    style,
    textColor,
    labelColor,
    card,
    isFlipped,
    setIsFlipped,
    formatBackText,
    backTextRendered,
    setShowDeleteConfirm,
  };

  return (
    <div className="open-card-view">
      {showDeleteConfirm && (
        <div className="delete-confirm-modal">
          <div className="delete-confirm-content">
            <h3>Удалить карту?</h3>
            <p>Вы уверены, что хотите удалить эту карту?</p>
            <div className="delete-confirm-buttons">
              <button type="button" className="confirm-button" onClick={async () => {
                setIsDeleting(true);
                try {
                  const response = await fetch('/telegram/del-card', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ cardId: card._id }),
                  });
                  if (response.ok) {
                    if (onDelete) onDelete(card.id);
                    if (window.Telegram?.WebApp) window.Telegram.WebApp.close();
                    else onBack();
                  } else {
                    alert('Не удалось удалить карту. Попробуйте позже.');
                  }
                } catch {
                  alert('Произошла ошибка при удалении карты.');
                } finally {
                  setIsDeleting(false);
                  setShowDeleteConfirm(false);
                }
              }} disabled={isDeleting}>
                {isDeleting ? 'Удаление...' : 'Да, удалить'}
              </button>
              <button type="button" className="cancel-button" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      <button type="button" className="open-card-back-btn" onClick={onBack}>
        <span className="open-card-back-btn-icon" aria-hidden="true">←</span>
        <span className="open-card-back-btn-label">Назад к картам</span>
      </button>

      <div className={`open-card-scene ${isFlipped ? 'is-flipped' : ''}`}>
        <div className="open-card-pivot">
          <OpenCardFace {...faceProps} side="front" />
          <OpenCardFace {...faceProps} side="back" />
        </div>
      </div>
    </div>
  );
};

const OpenCardFace = ({
  side, style, textColor, labelColor, card, isFlipped, setIsFlipped, formatBackText, backTextRendered, setShowDeleteConfirm,
}) => (
  <div className={getOpenCardFaceClasses({ side })}>
    <OpenCardFaceBackground
      mainColor={style.mainColor}
      cardStyle={style.cardStyle}
      backgroundPattern={style.backgroundPattern}
      gradientColor={style.gradientColor}
      gradientAngle={style.gradientAngle}
    />
    <div className="open-card-content">
      {side === 'front' ? (
        <>
          <div className="open-card-top">
            <div className="open-card-stat">
              <span className="open-card-stat-label" style={{ color: labelColor }}>бонус</span>
              <span className="open-card-stat-value" style={{ color: textColor }}>{style.cashback}%</span>
            </div>
            <div className="open-card-stat open-card-stat--right">
              <span className="open-card-stat-label" style={{ color: labelColor }}>баланс</span>
              <span className="open-card-stat-value" style={{ color: textColor }}>
                {card.balance.toLocaleString('ru-RU')}
              </span>
            </div>
          </div>

          <div className="open-card-qr">
            <QRCodeCanvas value={card.cardNumber} size={120} />
            <p className="open-card-qr-text" style={{ color: textColor }}>{card.cardNumber}</p>
          </div>

          <div className="open-card-owner">
            <span className="open-card-owner-label" style={{ color: labelColor }}>владелец</span>
            <span className="open-card-owner-name" style={{ color: textColor }}>
              {card.name} {card.surname}
            </span>
          </div>

          <button
            type="button"
            className="open-card-more-btn"
            style={{ color: labelColor }}
            onClick={() => setIsFlipped(!isFlipped)}
          >
            ···
          </button>
        </>
      ) : (
        <>
          <button type="button" className="open-card-delete-btn" onClick={() => setShowDeleteConfirm(true)}>
            🗑️
          </button>
          <h3 className="open-card-back-title" style={{ color: labelColor }}>
            {card.user?.accountName || 'Информация'}
          </h3>
          <div className="open-card-back-text" style={{ color: textColor }}>
            {formatBackText(backTextRendered)}
            {style.referralEnabled && style.referralCode && (
              <p className="open-card-referral" style={{ color: textColor }}>
                Ваш код для друзей: <strong>{style.referralCode}</strong>
              </p>
            )}
          </div>
          <button
            type="button"
            className="open-card-flip-back-btn"
            style={{ color: labelColor }}
            onClick={() => setIsFlipped(!isFlipped)}
          >
            Назад к карте
          </button>
        </>
      )}
    </div>
  </div>
);

export default CardDetail;
